import React, { useEffect, useMemo, useRef, useState } from "react";
import "./Landing.css";

/* ------------------------------------------------------------------
   MG HECTOR - PERSONALISATION SPLASH
   Cinematic boot sequence for the accessories/personalisation journey.

   Timeline (percentages map to the loading progress):
     00-15%  fade up from black, Hector silhouette emerges
     15-35%  thin scan beam travels across the vehicle
     35-55%  headlights bloom once, then settle
     55-75%  cabin lifts out of shadow
     75-90%  environment lifts, vehicle fully lit
     90-100% "PERSONALISATION READY", then hand off
   ------------------------------------------------------------------ */

const LOAD_DURATION_MS = 5200;
// Beat at 100% so "PERSONALISATION READY" registers before the hand-off.
const HOLD_MS = 620;
const OUTRO_MS = 1000;

// Progress thresholds that drive the staged reveal.
const STAGES = [
  { at: 0, key: "ignite", caption: "INITIALISING EXPERIENCE" },
  { at: 15, key: "scan", caption: "INITIALISING EXPERIENCE" },
  { at: 35, key: "illuminate", caption: "CALIBRATING COMFORT" },
  { at: 55, key: "cabin", caption: "PREPARING YOUR SPACE" },
  { at: 75, key: "settle", caption: "PREPARING YOUR SPACE" },
  { at: 96, key: "ready", caption: "PERSONALISATION READY" },
];

const stageFor = (progress) =>
  STAGES.reduce((acc, stage) => (progress >= stage.at ? stage : acc), STAGES[0]);

// easeInOutCubic - slow start, controlled finish. Keeps the reveal luxurious
// rather than linear/mechanical.
const easeInOutCubic = (t) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

/* Shallow speedometer arc.
   A wide, low-rise circular segment - deliberately flat so it reads as a
   precision instrument rather than a gauge dial. Geometry is derived from
   the chord width and rise, and the exact arc length is computed so
   stroke-dashoffset maps 1:1 to progress. */
const ARC = (() => {
  const width = 460; // chord
  const rise = 34; // how far the centre dips below the ends
  const pad = 3; // room for the stroke + head glow

  // Circle through both ends and the midpoint.
  const half = width / 2;
  const radius = (half * half + rise * rise) / (2 * rise);

  const x0 = pad;
  const x1 = width - pad;
  const y0 = pad;
  const yMid = pad + rise;

  // Sweep angle of the segment, for the true arc length.
  const theta = 2 * Math.asin(half / radius);

  return {
    width,
    height: Math.ceil(yMid + pad),
    // Single arc: left end -> right end, bulging downward.
    d: `M ${x0} ${y0} A ${radius} ${radius} 0 0 0 ${x1} ${y0}`,
    length: radius * theta,
    radius,
  };
})();

const MgWordmark = ({ className }) => (
  // Official MG octagon, recoloured white for the dark stage.
  // Source: hd-logo-dsc-065.webp (mgmotor.co.in) -> public/mg-logo.png
  <img className={className} src="/mg-logo.png" alt="MG" draggable="false" />
);

const LandingPage = ({ onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [isLeaving, setIsLeaving] = useState(false);
  // Measured from the rendered path - the closed-form arc length is a
  // hair off what the browser actually draws, which leaves a visible
  // sliver unfilled at 100%.
  const [arcLength, setArcLength] = useState(ARC.length);
  const arcRef = useRef(null);
  const frameRef = useRef(0);
  const doneRef = useRef(false);

  useEffect(() => {
    if (arcRef.current?.getTotalLength) {
      setArcLength(arcRef.current.getTotalLength());
    }
  }, []);

  const prefersReducedMotion = useMemo(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
    []
  );

  useEffect(() => {
    // Reduced motion: skip the cinematic build, hand off almost immediately.
    if (prefersReducedMotion) {
      setProgress(100);
      const t = window.setTimeout(() => onComplete?.(), 600);
      return () => window.clearTimeout(t);
    }

    const startedAt = performance.now();

    // rAF rather than setInterval so the counter stays locked to the
    // compositor and the number never stutters against the animation.
    const tick = (now) => {
      const linear = Math.min(1, (now - startedAt) / LOAD_DURATION_MS);
      setProgress(Math.round(easeInOutCubic(linear) * 100));

      if (linear < 1) {
        frameRef.current = requestAnimationFrame(tick);
        return;
      }

      if (!doneRef.current) {
        doneRef.current = true;
        // Hold on the completed state, then dissolve away.
        window.setTimeout(() => {
          setIsLeaving(true);
          window.setTimeout(() => onComplete?.(), OUTRO_MS);
        }, HOLD_MS);
      }
    };

    frameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameRef.current);
  }, [onComplete, prefersReducedMotion]);

  const stage = stageFor(progress);

  return (
    <main
      className={`mg-splash is-${stage.key}${isLeaving ? " is-leaving" : ""}`}
      data-progress={progress}
      aria-label="Preparing your MG personalisation experience"
    >
      {/* Ambient environment ------------------------------------------- */}
      <div className="mg-splash__grid" aria-hidden="true" />
      <div className="mg-splash__horizon" aria-hidden="true" />
      <div className="mg-splash__vignette" aria-hidden="true" />

      {/* Brand ---------------------------------------------------------- */}
      <header className="mg-splash__brand">
        <MgWordmark className="mg-splash__logo" />
        <span className="mg-splash__brand-rule" aria-hidden="true" />
        <span className="mg-splash__brand-tag">HECTOR</span>
      </header>

      {/* Vehicle hero --------------------------------------------------- */}
      <section className="mg-splash__stage" aria-hidden="true">
        {/* The frame shrink-wraps the painted image (width:max-content) so every
            overlay percentage below resolves against the CAR, not the viewport.
            Without this the glows drift as the aspect ratio changes. */}
        <div className="mg-splash__car-wrap">
          <div className="mg-splash__car-frame">
            <img
              className="mg-splash__car"
              src="/mg-hector-front-cutout.png"
              alt=""
              draggable="false"
            />

            {/* Headlight bloom - positioned over the LED signature in the art */}
            <span className="mg-splash__beam mg-splash__beam--left" />
            <span className="mg-splash__beam mg-splash__beam--right" />
            <span className="mg-splash__fog mg-splash__fog--left" />
            <span className="mg-splash__fog mg-splash__fog--right" />
          </div>
        </div>

        <div className="mg-splash__reflection" />
      </section>

      {/* Message + progress ---------------------------------------------
          Everything centres on the vertical axis: numeral, then the arc
          sweeping beneath it, then the caption. */}
      <section className="mg-splash__readout">
        <p className="mg-splash__promise">
          <span>Crafted</span> <span>around</span> <span>you</span>
        </p>

        <div className="mg-splash__panel">
          <span className="mg-splash__count">
            {String(progress).padStart(2, "0")}
            <i>%</i>
          </span>

          {/* Shallow speedometer-style sweep. The arc is drawn once and
              revealed with stroke-dashoffset, so progress animates on the
              GPU without re-drawing the path. */}
          <svg
            className="mg-splash__arc"
            viewBox={`0 0 ${ARC.width} ${ARC.height}`}
            preserveAspectRatio="xMidYMid meet"
            aria-hidden="true"
            focusable="false"
          >
            <path className="mg-splash__arc-track" d={ARC.d} />
            <path
              ref={arcRef}
              className="mg-splash__arc-fill"
              d={ARC.d}
              style={{
                strokeDasharray: arcLength,
                strokeDashoffset: arcLength * (1 - progress / 100),
              }}
            />
          </svg>

          <span
            key={stage.caption}
            className="mg-splash__caption"
            role="status"
            aria-live="polite"
          >
            {stage.caption}
          </span>
        </div>
      </section>
    </main>
  );
};

export default LandingPage;
